import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { 
    Grid, 
    InputAdornment, 
    Typography, 
    Button,
    Autocomplete,
    Switch,
    TextField,
} from '@mic3/platform-ui';
import moment from 'moment';
import { bind } from 'app/utils/decorators/decoratorUtils';
import ModalDialog from 'app/components/organisms/ModalDialog/ModalDialog';
import DateTimePicker from 'app/containers/Designer/Form/components/DateTime';
import Icon from 'app/components/atoms/Icon/Icon';

const unitOptions = [
    { label: 'Minute(s)', value: 'm' },
    { label: 'Hour(s)', value: 'h' },
    { label: 'Day(s)', value: 'd' },
    { label: 'Month(s)', value: 'M' },
    { label: 'Year(s)', value: 'Y' },
];

const rangeOptions = [
    { label: 'Last', value: 'subtract' },
    { label: 'Next', value: 'add' },
];

const defaultRelativeValue = (unit = 'm') => ({ relative: true, range: 'subtract', amount: 1, unit });

class GlobalSearchDateRangePicker extends PureComponent {
    static propTypes = {
        title: PropTypes.string.isRequired,
        value: PropTypes.any,
        onChange: PropTypes.func.isRequired,
        onClose: PropTypes.func.isRequired,
        variant: PropTypes.oneOf(['all', 'relative', 'standard']),
    };

    static defaultProps = {
        variant: 'all',
    };

    constructor(props) {
        super(props);
        
        const { value, variant } = props;
        const [start, end] = (Array.isArray(value) && value.map((date) => new Date(date))) || [null, null];
        
        const relative = value && value.relative
            ? true
            : value && Array.isArray(value)
                ? false
                : !['standard'].includes(variant);

        this.state = {
            showModal: true,
            start,
            end,
            value: value || null,
            errors: false,
            relative,
        };
    }

    @bind
    onClearRelative() {
        this.setState({ start: null, end: null, value: null });
    }

    @bind
    onSave() {
        const { onChange } = this.props;
        const { value } = this.state;
        
        const currentDate = new Date();
        const calculatedRelativeDate =
            value?.relative &&
            moment(currentDate)
                [value.range](value.amount, value.unit)
                .toDate();

        if (value?.relative && !value?.amount) {
            return this.setState({ errors: { amount: 'Amount is required.' } });
        }

        if (
            value?.relative &&
            value?.amount &&
            (isNaN(calculatedRelativeDate) ||
             moment('1970-01-01', 'YYYY-MM-DD').toDate() > calculatedRelativeDate ||
             moment('4821-12-26', 'YYYY-MM-DD').toDate() < calculatedRelativeDate)
        ) {
            return this.setState({ errors: { amount: 'Amount is out of range.' } });
        }

        onChange(value);
        this.closeModal();
    }

    @bind
    onChange() {
        const { start, end } = this.state;
        const value = start && [start, end];
        this.setState({ value });
    }

    @bind
    onChangeRelative(evnt) {
        const { value } = this.state;
        const { target } = evnt;
        const nextState = {
            value: value && typeof value === 'object'
                ? {
                    ...defaultRelativeValue('m'),
                    ...value,
                    [target.name]: target.value,
                }
                : { ...defaultRelativeValue('m'), [target.name]: target.value },
        };

        if (nextState.value.amount < 0) {
            nextState.value.amount = 1;
        }

        if (nextState.value.amount) {
            nextState.errors = null;
        }

        this.setState(nextState);
    }

    @bind
    onChangeStart({ target: { value } }) {
        let { end } = this.state;
        let start = value && new Date(value);

        if (!start) {
            end = null;
        } else if (!end) {
            end = new Date(start);
            end.setHours(23, 59, 59, 999);
        } else if (start.getTime() >= end.getTime()) {
            start = new Date(end.getTime());
        }

        start && start.setMilliseconds(0);
        this.setState({ start, end, value: [start, end] }, this.onChange);
    }

    @bind
    onChangeEnd({ target: { value } }) {
        let { start } = this.state;
        let end = value && new Date(value);

        if (end && !this.state.end) {
            end.setHours(23, 59, 59, 999);
        }

        if (!end) {
            start = null;
        } else if (!start) {
            start = new Date(end);
            start.setHours(0, 0, 0, 0);
        } else if (start.getTime() >= end.getTime()) {
            end = new Date(start.getTime());
        }

        end && end.setMilliseconds(999);
        this.setState({ start, end, value: [start, end] }, this.onChange);
    }

    @bind
    toggleRelative({ target: { value: checked } }) {
        const { value } = this.props;
        const nextState = { relative: checked, value: null, start: null, end: null };

        if (checked && value && typeof value === 'object') {
            nextState.value = value;
        }

        if (!checked && value && Array.isArray(value)) {
            const [start, end] = value;
            nextState.value = value;
            nextState.start = start;
            nextState.end = end;
        }

        this.setState(nextState);
    }

    @bind
    closeModal() {
        this.setState({ showModal: false });
        this.props.onClose();
    }

    render() {
        const { title, variant } = this.props;
        const { errors, start, end, relative, showModal, value } = this.state;

        return (
            <ModalDialog
                open={showModal}
                onClose={this.closeModal}
                title={title}
                actions={
                    <>
                        <Button onClick={this.closeModal} variant="text">
                            Cancel
                        </Button>
                        <Button onClick={this.onSave} variant="contained" color="primary">
                            Apply Filter
                        </Button>
                    </>
                }
                footer={<Typography variant="caption">* All fields are required</Typography>}
            >
                {variant === 'all' && (
                    <Switch
                        label="Relative time"
                        onChange={this.toggleRelative}
                        value={relative}
                        style={{ marginLeft: 0 }}
                    />
                )}
                
                {relative && (
                    <Grid container wrap="nowrap" justify="space-around">
                        <Autocomplete
                            error={!!errors?.range}
                            label="Range"
                            required
                            name="range"
                            onChange={this.onChangeRelative}
                            options={rangeOptions}
                            valueField="value"
                            value={value ? value.range || null : null}
                            clearable={false}
                        />
                        <div style={{ marginRight: '4px' }} />
                        <TextField
                            error={!!errors?.amount}
                            helperText={errors?.amount}
                            required
                            name="amount"
                            label="Amount"
                            type="number"
                            onChange={this.onChangeRelative}
                            value={value ? value.amount || null : null}
                            clearable={false}
                        />
                        <div style={{ marginRight: '4px' }} />
                        <Autocomplete
                            error={!!errors?.unit}
                            valueField="value"
                            label="Unit"
                            required
                            name="unit"
                            onChange={this.onChangeRelative}
                            options={unitOptions}
                            value={value ? value.unit || null : null}
                            clearable={false}
                        />
                    </Grid>
                )}
                
                {!relative && (
                    <Grid container wrap="nowrap">
                        <DateTimePicker
                            label="From"
                            showTodayButton
                            ampm={false}
                            value={start}
                            format="DD, MMM YYYY HH:mm"
                            onChange={this.onChangeStart}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon name="calendar-blank" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <div style={{ marginRight: '4px' }} />
                        <DateTimePicker
                            label="To"
                            showTodayButton
                            ampm={false}
                            value={end}
                            format="DD, MMM YYYY HH:mm"
                            onChange={this.onChangeEnd}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon name="calendar-blank" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                )}
                
                <Grid container style={{ margin: '8px 0' }}>
                    <Button
                        startIcon={<Icon name="close" size="sm" />}
                        onClick={this.onClearRelative}
                        variant="outlined"
                        color="primary"
                    >
                        Clear
                    </Button>
                </Grid>
            </ModalDialog>
        );
    }
}

export default GlobalSearchDateRangePicker;

